import { useForm } from "react-hook-form"
import { Button } from "./ui/button"
import { DialogHeader, DialogFooter, Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogClose } from "./ui/dialog"
import { Input } from "./ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, FormControl, FormField, FormItem, FormMessage } from "./ui/form"
import { unlockSchema } from "@/schemas/unlockSchema"
import { unlockUser } from "@/actions/unlock"
import { z as builder } from "zod"
import { useCallback, useState } from "react"
import { Alert, AlertDescription } from "./ui/alert"
import { toast } from "sonner"

const UserUnlock = () => {
    const [isOpen, setOpen] = useState(false)
    const [alertVisible, toggleAlert] = useState(false)
    const unlock = useForm<builder.infer<typeof unlockSchema>>({
        resolver: zodResolver(unlockSchema),
        mode: "all",
        reValidateMode: "onChange",
    })

    const closingHandler = useCallback((opened: boolean) => {
        setOpen(opened)

        if (!opened) {
            unlock.reset()
            unlock.clearErrors("phoneNumber")

            toggleAlert(false)
        }
    }, [unlock])

    const verifyIntegrity = useCallback(async (state: builder.infer<typeof unlockSchema>) => {
        const validator = new RegExp(/^\+?[1-9]\d{1,14}$/)
        const normalized = state.phoneNumber.replaceAll("-", "").trim()

        const isValid = validator.test(normalized)
        if (!isValid) {
            unlock.setError("phoneNumber", {
                message: "Solo se permiten números y guiones (-)"
            })

            return
        }

        const result = await unlockUser(state)

        const actions: Record<string, () => void> = {
            UNLOCKED: () => toast.success("Usuario desbloqueado"),
            NOT_BLOCKED: () => toast.info("El usuario no está bloqueado"),
            USER_DISABLED: () => toast.error("Tu usuario no es apto para desbloqueo"),
            ZONE_BLOCKED: () => toast.warning("Tu zona no es válida para desbloqueo"),
            USER_NOT_FOUND: () => toast.error("Usuario no encontrado")
        }

        setOpen(false);
        unlock.reset()
        
        const fn = actions[result.data.reason]

        if (!fn) return toast.error("Error al desbloquear");
        fn();
    }, [unlock])

    return (
        <Dialog open={isOpen} onOpenChange={closingHandler}>
            <DialogTrigger asChild>
                <Button type="button" className="cursor-pointer h-10 w-full" variant="outline">
                    Desbloquear
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-106.25">
                <Form {...unlock}>
                    <form className="space-y-6" onSubmit={unlock.handleSubmit(verifyIntegrity)}>
                        <DialogHeader>
                            <DialogTitle>Desbloqueo de usuario</DialogTitle>
                            <DialogDescription>
                                <span className="font-bold">Ingresa a 10 dígitos</span>,{" "}
                                el número con el que te registraste en OPI o Chatbot Victoria Fuller
                            </DialogDescription>
                        </DialogHeader>

                        <div>
                            <FormField
                                control={unlock.control}
                                name="phoneNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input type="text" {...field} required autoComplete="phoneNumber" placeholder="Ej: 111-234-25-32" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Alert data-hidden={alertVisible} className="my-4 bg-amber-50 data-[hidden='false']:hidden block">
                                <AlertDescription>
                                    El número telefónico ingresado no se encuentra registrado
                                </AlertDescription>
                            </Alert>

                        </div>

                        <DialogFooter className="justify-center">
                            <DialogClose asChild className="w-full sm:w-1/2">
                                <Button className="cursor-pointer" variant="outline">Cancelar</Button>
                            </DialogClose>

                            <Button type="submit" className="w-full sm:w-1/2 cursor-pointer bg-red-500 hover:bg-red-600">Desbloquear</Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default UserUnlock
